import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, Users, TrendingUp, Award, Globe, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function AboutPage() {
  const navigate = useNavigate();

  const values = [
    { icon: <Heart className="w-8 h-8" />, title: 'Customer First', desc: 'Every decision we make puts our customers at the center' },
    { icon: <Target className="w-8 h-8" />, title: 'Innovation', desc: 'Constantly pushing boundaries to deliver better solutions' },
    { icon: <Users className="w-8 h-8" />, title: 'Integrity', desc: 'Transparent, honest, and ethical in all our dealings' },
    { icon: <Award className="w-8 h-8" />, title: 'Excellence', desc: 'Committed to delivering world-class financial services' }
  ];

  const milestones = [
    { year: '2022', title: 'Founded', desc: 'Britedge was born with a vision to revolutionize African fintech' },
    { year: '2023', title: '10,000 Users', desc: 'Reached our first major milestone with rapid growth' },
    { year: '2024', title: 'CBN License', desc: 'Received full regulatory approval from the Central Bank of Nigeria' },
    { year: '2024', title: '50,000+ Users', desc: 'Now serving thousands of Nigerians daily' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 md:px-8 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl md:text-7xl font-bold font-heading text-slate-900 mb-6">
            Building The Future Of African Finance
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Britedge is on a mission to provide every African with access to world-class financial services, making banking faster, cheaper, and more accessible than ever before.
          </p>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-12 text-white">
              <Target className="w-12 h-12 mb-6" />
              <h2 className="text-3xl font-bold font-heading mb-4">Our Mission</h2>
              <p className="text-blue-100 text-lg leading-relaxed">
                To democratize financial services across Africa by providing innovative, secure, and affordable banking solutions that empower individuals and businesses to achieve their financial goals.
              </p>
            </div>
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-12 text-white">
              <Globe className="w-12 h-12 mb-6" />
              <h2 className="text-3xl font-bold font-heading mb-4">Our Vision</h2>
              <p className="text-purple-100 text-lg leading-relaxed">
                To become Africa's most trusted and widely-used fintech platform, driving financial inclusion and enabling millions to build wealth, access credit, and secure their financial future.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-20 px-4 md:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold font-heading text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Britedge was founded by a team of passionate technologists and financial experts who saw firsthand the challenges Africans face in accessing quality financial services. Long queues at banks, high transaction fees, and limited access to credit were everyday realities.
                </p>
                <p>
                  We set out to change this narrative. Starting with a simple mobile app, we built a platform that makes sending money as easy as sending a text message. But we didn't stop there.
                </p>
                <p>
                  Today, Britedge is a comprehensive financial ecosystem offering everything from instant transfers and bill payments to savings goals, micro-loans, and innovative cooperative banking features like Britedge savers circle - bringing traditional African savings methods into the digital age.
                </p>
                <p>
                  With over thousands of active users funds fully managed by Safe Haven MFB, we're just getting started on our mission to transform African finance.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1752343809373-0600ccb3bcd2?q=80&w=2070&auto=format&fit=crop" 
                alt="Team collaboration" 
                className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              These principles guide everything we do at Britedge
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, idx) => (
              <div key={idx} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200 hover:border-brand-blue hover:shadow-md transition-all">
                <div className="w-16 h-16 rounded-2xl bg-blue-100 text-brand-blue flex items-center justify-center mb-6">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold font-heading text-slate-900 mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Milestones Section */}
      {/* <section className="py-20 px-4 md:px-8 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading mb-4">Our Journey</h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              Key milestones in Britedge's growth story
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {milestones.map((milestone, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                  <div className="text-5xl font-bold text-brand-blue mb-4">{milestone.year}</div>
                  <h3 className="text-xl font-bold mb-2">{milestone.title}</h3>
                  <p className="text-slate-300 text-sm leading-relaxed">{milestone.desc}</p>
                </div>
                {idx < milestones.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-white/20"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Impact Section */}
      {/* <section className="py-20 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold font-heading text-slate-900 mb-4">Our Impact</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Real numbers that show how we're transforming lives
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold font-heading text-brand-blue mb-2">50,000+</div>
              <p className="text-slate-600">Active Users</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold font-heading text-brand-blue mb-2">₦2.5B+</div>
              <p className="text-slate-600">Transactions Processed</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold font-heading text-brand-blue mb-2">1,200+</div>
              <p className="text-slate-600">Cooperative Groups</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold font-heading text-brand-blue mb-2">₦500M+</div>
              <p className="text-slate-600">Loans Disbursed</p>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 px-4 md:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold font-heading mb-6">Join The Britedge Movement</h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Be part of the financial revolution. Start your journey with Britedge today.
          </p>
          <Button 
            onClick={() => navigate('/auth')}
            className="bg-white text-brand-blue hover:bg-blue-50 rounded-full px-10 py-7 text-xl font-semibold shadow-2xl transition-all active:scale-95"
          >
            Open Your Free Account
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
